import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { systemHealthApi } from '../../services/admin/systemHealthApi'
import type {
  SystemHealthConfig,
  SystemHealthRecipientInput,
} from '../../types/systemHealth'
import {
  HEALTH_POLL_MS,
  ACTIVITY_POLL_MS,
  STATS_POLL_MS,
  SIDEBAR_DOT_POLL_MS,
  SERVICE_BUS_POLL_MS,
} from '../../constants/systemHealth'

const CONFIG_KEY = ['admin', 'system-health', 'config']
const RECIPIENTS_KEY = ['admin', 'system-health', 'recipients']

const errMessage = (error: any, fallback: string) =>
  error?.response?.data?.error?.message || error?.message || fallback

export const useSystemHealthSummaryQuery = () =>
  useQuery({
    queryKey: ['admin', 'system-health', 'summary'],
    queryFn: systemHealthApi.summary,
    refetchInterval: HEALTH_POLL_MS,
    refetchIntervalInBackground: false,
  })

export const useSystemHealthActivityQuery = () =>
  useQuery({
    queryKey: ['admin', 'system-health', 'activity'],
    queryFn: systemHealthApi.activity,
    refetchInterval: ACTIVITY_POLL_MS,
    refetchIntervalInBackground: false,
  })

export const useSystemHealthStatsQuery = () =>
  useQuery({
    queryKey: ['admin', 'system-health', 'stats'],
    queryFn: systemHealthApi.stats,
    refetchInterval: STATS_POLL_MS,
    refetchIntervalInBackground: false,
  })

export const useServiceBusHealthQuery = () =>
  useQuery({
    queryKey: ['admin', 'system-health', 'service-bus'],
    queryFn: systemHealthApi.serviceBus,
    refetchInterval: SERVICE_BUS_POLL_MS,
    refetchIntervalInBackground: false,
  })

/**
 * Terminally-failed messages. Polled on the slow interval, not the 15s one:
 * the count spans every hypertable chunk (~340ms to plan) and the number only
 * moves when something actually fails, which is rare by design.
 */
export const useFailedMessagesQuery = (days: number) =>
  useQuery({
    queryKey: ['admin', 'system-health', 'failed', days],
    queryFn: () => systemHealthApi.failed(days),
    refetchInterval: STATS_POLL_MS,
    refetchIntervalInBackground: false,
    // Keeps the previous window on screen while the next one loads, so the
    // numbers do not blank out on every range click.
    placeholderData: (prev) => prev,
  })

/**
 * The processor's build and runtime. Deliberately NOT polled: it only changes
 * on a deploy or a stack flip, both of which reload the page in practice.
 */
export const useProcessorVersionQuery = () =>
  useQuery({
    queryKey: ['admin', 'system-health', 'version'],
    queryFn: systemHealthApi.processorVersion,
    staleTime: Infinity,
  })

export const useSystemHealthDotQuery = () =>
  useQuery({
    queryKey: ['admin', 'system-health', 'dot'],
    queryFn: systemHealthApi.summary,
    refetchInterval: SIDEBAR_DOT_POLL_MS,
    refetchIntervalInBackground: false,
  })

// --- Alert config ---

export const useSystemHealthConfig = () =>
  useQuery({ queryKey: CONFIG_KEY, queryFn: () => systemHealthApi.getConfig() })

export const useUpdateSystemHealthConfig = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: Partial<SystemHealthConfig>) => systemHealthApi.updateConfig(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONFIG_KEY })
      toast.success('Settings saved')
    },
    onError: (error: any) => toast.error(errMessage(error, 'Failed to save settings')),
  })
}

// --- Alert recipients ---

export const useSystemHealthRecipients = () =>
  useQuery({ queryKey: RECIPIENTS_KEY, queryFn: () => systemHealthApi.listRecipients() })

export const useCreateSystemHealthRecipient = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: SystemHealthRecipientInput) => systemHealthApi.createRecipient(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RECIPIENTS_KEY })
      toast.success('Recipient added')
    },
    onError: (error: any) => toast.error(errMessage(error, 'Failed to add recipient')),
  })
}

export const useUpdateSystemHealthRecipient = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: Partial<SystemHealthRecipientInput> }) =>
      systemHealthApi.updateRecipient(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RECIPIENTS_KEY })
      toast.success('Recipient updated')
    },
    onError: (error: any) => toast.error(errMessage(error, 'Failed to update recipient')),
  })
}

export const useDeleteSystemHealthRecipient = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => systemHealthApi.deleteRecipient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RECIPIENTS_KEY })
      toast.success('Recipient removed')
    },
    onError: (error: any) => toast.error(errMessage(error, 'Failed to remove recipient')),
  })
}

// --- Send test status report ---

export const useSendSystemHealthReport = () =>
  useMutation({
    mutationFn: () => systemHealthApi.sendReport(),
    onSuccess: (result) => {
      const count = result.recipients
      toast.success(
        `Report queued to ${count} recipient${count === 1 ? '' : 's'} (${result.emails} email, ${result.sms} SMS) — arriving in ~${result.estimatedDeliverySeconds}s`
      )
    },
    onError: (error: any) => toast.error(errMessage(error, 'Failed to send report')),
  })
